<template>
  <div>
    <div v-if="patientId">
      <b-card-group>
        <div style="font-size: 14pt;" class="mb-3">
          <router-link
            tag="a"
            :to="'/patients/' + patientId"
            v-b-tooltip.hover
            :title="$t('Открыть')"
          >
            <b-button
              class="float-left mr-3"
              right
              size="sm"
              variant="outline-secondary"
            >
              <font-awesome-icon icon="chevron-left" />
              {{ $t('К пациенту') }}
            </b-button>
          </router-link>
          <b>{{ $t('Пациент') }}: </b>
          <span>
            <span v-html="this.common"></span>
          </span>
        </div>
      </b-card-group>
      <b-card-group>
        <b-card no-body header-tag="header" footer-tag="footer">
          <h6 v-b-toggle.filterCollapse slot="header" class="mb-0">
            {{ $t('Фильтр') }}
            <b-button
              v-on:click="reset"
              size="sm"
              variant="secondary"
              class="ml-2"
            >
              <font-awesome-icon icon="times" />
              {{ $t('Сброс') }}
            </b-button>
          </h6>
          <b-collapse visible id="filterCollapse">
            <div class="card-body">
              <div>
                <b-form>
                  <b-row>
                    <b-col cols="1">
                      <label for="hla">
                        HLA
                      </label>
                    </b-col>
                  </b-row>
                  <b-row class="mb-3 no-gutters">
                    <b-col
                      :cols="12"
                      :sm="8"
                      :md="4"
                      :lg="4"
                      :xl="2"
                      v-for="item in sortedHlas"
                      :key="item.hlaTypeValue"
                    >
                      {{ item.hlaTypeName }}
                      <b-input-group>
                        <b-form-input
                          v-mask="{ mask: '*{2}(:*{2,3})|(:*{2}:*{2}[A])' }"
                          v-model="item.firstAllele"
                          :disabled="true"
                        ></b-form-input>
                        <b-form-input
                          v-mask="{ mask: '*{2}(:*{2,3})|(:*{2}:*{2}[A])' }"
                          v-model="item.secondAllele"
                          :disabled="true"
                        ></b-form-input>
                      </b-input-group>
                    </b-col>
                  </b-row>
                  <b-row>
                    <filter-age-select
                      :ageFrom="form.ageFrom"
                      :ageTo="form.ageTo"
                      @input="ageHandler"
                    ></filter-age-select>
                    <filter-select
                      :label="$t('Степень совместимости')"
                      :cols="4"
                      v-model="form.coincidence"
                      :withoutEmpty="true"
                      :options="[
                        { value: $t('Степень совместимости') + ' 7 / 10', key: 7 },
                        { value: $t('Степень совместимости') + ' 8 / 10', key: 8 },
                        { value: $t('Степень совместимости') + ' 9 / 10', key: 9 },
                        { value: $t('Степень совместимости') + ' 10 / 10', key: 10 },
                      ]"
                    ></filter-select>
                    <filter-select
                      label="Пол"
                      v-model="form.gender"
                      :options="genders"
                    ></filter-select>
                    <filter-input
                      label="ID "
                      v-model="form.externalId"
                    ></filter-input>
                  </b-row>
                </b-form>
              </div>
            </div>
            <footer class="card-footer">
              <div class="float-right">
                <b-button
                  v-on:click="searchForPatient"
                  size="sm"
                  variant="primary"
                >
                  <font-awesome-icon icon="search" />
                  {{ $t('Поиск') }}
                </b-button>
                <b-button
                  v-on:click="resetForPatient"
                  size="sm"
                  variant="default"
                >
                  <font-awesome-icon icon="times" />
                  {{ $t('Сброс') }}
                </b-button>
              </div>
              <div class="clearfix"></div>
            </footer>
          </b-collapse>
        </b-card>
      </b-card-group>
    </div>
    <div v-else>
      <b-card-group>
        <b-card no-body>
          <b-collapse visible id="filterCollapse">
            <div class="card-body donor-at-work-form">
              <div>
                <b-form>
                  <b-row>
                    <b-col cols="1">
                      <b-button v-on:click="resetFilter" size="sm" variant="secondary" class="ml-2">
                        <font-awesome-icon icon="times" />
                        {{ $t('Сброс') }}
                      </b-button>
                    </b-col>
                    <filter-select
                      :label="$t('Статус')"
                      v-model="form.donorStatuses"
                      :cols="2"
                      v-on:onChange="search"
                      v-on:input="search"
                      :multiple="true"
                      :empty-as-null="true"
                      :options="statuses"
                    ></filter-select>
                    <filter-select
                      :cols="1"
                      :label="$t('Код РР')"
                      :disabled="$auth.user().isLr"
                      v-on:onChange="search"
                      v-model="form.lrOrganizationId"
                      :options="lrOrganizations"
                    ></filter-select>
                    <filter-input
                      label="ID "
                      v-on:onBlur="search"
                      :cols="1"
                      v-model="form.externalId"
                    ></filter-input>
                    <filter-select
                      :cols="1"
                      :label="$t('Код ТЦ')"
                      v-on:onChange="search"
                      v-model="form.tcOrganizationId"
                      :options="tcOrganizations"
                    ></filter-select>
                    <filter-select
                      :cols="2"
                      :label="$t('Координатор регистра')"
                      v-on:onChange="search"
                      v-model="form.operatorId"
                      :options="operators"
                    ></filter-select>
                  </b-row>
                </b-form>
              </div>
            </div>
          </b-collapse>
        </b-card>
      </b-card-group>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import BaseFilter from '@/models/base.filter';

export default {
  extends: BaseFilter,
  name: 'filter-form',
  props: [
    'patientId',
    'patient',
    'ageFrom',
    'ageTo',
    'coincidence',
    'gender',
    'donorStatuses',
    'operatorId',
    'externalId',
    'lrOrganizationId',
    'tcOrganizationId',
  ],
  data() {
    return {
      form: {
        ageFrom: 18,
        ageTo: 65,
        coincidence: 10,
        gender: this.gender || null,
        donorStatuses: this.donorStatuses || [],
        operatorId: this.operatorId || null,
        externalId: this.externalId || '',
        lrOrganizationId: this.lrOrganizationId || null,
        tcOrganizationId: this.tcOrganizationId || null,
      },
      statuses: [],
      operators: [],
      genders: [],
      lrOrganizations: [],
      tcOrganizations: [],
    };
  },
  watch: {
    ageFrom: function() {
      this.form.ageFrom = this.ageFrom;
    },
    ageTo: function() {
      this.form.ageTo = this.ageTo;
    },
    coincidence: function() {
      this.form.coincidence = this.coincidence;
    },
    donorStatuses: function() {
      this.form.donorStatuses = this.donorStatuses;
    },
    operatorId: function() {
      this.form.operatorId = this.operatorId;
    },
    externalId: function() {
      this.form.externalId = this.externalId;
    },
  },
  methods: {
    getDictionaries() {
      if (!this.patientId) {
        axios
          .get('/Dictionary/GetDonorStatuses')
          .then((response) => {
            this.statuses = response.data;
          })
          .catch((w) => {});

        const orgId = this.$auth.user().isLr
          ? this.$auth.user().lrId ||
            this.$auth.user().impersonateOrganizationId ||
            this.$auth.user().organizationId
          : null;

        axios
          .get('/Dictionary/GetDonorOperators?organizationId=' + orgId)
          .then((response) => {
            this.operators = response.data;
          })
          .catch((w) => {});

        axios
          .get('/Dictionary/GetLrOrganizationCodes')
          .then((response) => {
            this.lrOrganizations = response.data;
          })
          .catch(() => {});

        axios
          .get('/Dictionary/GetTcOrganizationCodes')
          .then((response) => {
            this.tcOrganizations = response.data;
          })
          .catch(() => {});
      } else {
        if (this.patient && this.patient.status != 2) {
          Swal(
            '',
            this.$t('Для отправки доноров в ЛР необходим статус пациента Поиск донора'),
            'info'
          );
        }

        axios
          .get('/Dictionary/GetGenders')
          .then((response) => {
            this.genders = response.data;
          })
          .catch((w) => {});
      }
    },
    search: function() {
      this.$emit('search', {
        donorStatuses: this.form.donorStatuses,
        operatorId: this.form.operatorId,
        externalId: this.form.externalId,
        lrOrganizationId: this.form.lrOrganizationId,
        tcOrganizationId: this.form.tcOrganizationId,
      });
    },
    searchForPatient: function() {
      this.$emit('search', {
        ageFrom: this.form.ageFrom,
        ageTo: this.form.ageTo,
        coincidence: this.form.coincidence,
        gender: this.form.gender,
        externalId: this.form.externalId,
      });
    },
    resetForPatient: function() {
      this.$emit('reset');
      this.form.ageFrom = 18;
      this.form.ageTo = 65;
      this.form.coincidence = 10;
      this.form.externalId = '';
    },
    resetFilter(e) {
      this.reset(e, () => {
        if (this.$auth.user().isLr) {
          this.form.lrOrganizationId = this.$auth.user().currentOrganization.id;
        }
      });
    },
    ageHandler(e) {
      this.form.ageFrom = e.ageFrom;
      this.form.ageTo = e.ageTo;
    },
  },
  computed: {
    sortedHlas: function() {
      if (!this.patient || !this.patient.hlas) return [];

      let sorted = this.patient.hlas.slice();
      sorted.forEach(function(val) {
        if (val.hlaTypeName == 'DQB1*') {
          val.sortValue = val.hlaTypeValue + 1;
        } else if (val.hlaTypeName == 'DRB1*') {
          val.sortValue = val.hlaTypeValue - 1;
        } else {
          val.sortValue = val.hlaTypeValue;
        }
      });
      sorted.sort(function(a, b) {
        return a.sortValue - b.sortValue;
      });
      return sorted;
    },
    // isLrNameVisible() {
    //   return this.profile.isRdkm && !this.patientId;
    // },
    common: function() {
      const arr = [this.fullName, this.birthday, this.diagnosis];

      if (this.$auth.user().isRdkm) {
        arr.push(this.tc);
      }

      let result = '';
      arr.forEach(function(element) {
        if (element) {
          result += '. ' + element;
        }
      });
      if (result) {
        result = result.substring(2);
      }
      return result;
    },

    fullName: function() {
      if (!this.patient) return '';

      const arr = [
        this.patient.lastName,
        this.patient.firstName,
        this.patient.middleName,
      ];
      let result = '';
      arr.forEach(function(element) {
        if (element) {
          if (result) {
            result += ' ';
          }
          result += element;
        }
      });
      return result;
    },

    birthday: function() {
      if (!this.patient) return '';

      if (!this.patient.birthday) {
        return '';
      }
      return (
        '<b>' + this.$t('Дата рождения') + ': </b>' +
        moment(new Date(this.patient.birthday)).format('DD.MM.YYYY')
      );
    },

    diagnosis: function() {
      return '<b>' + this.$t('Диагноз') + ': </b>' + this.diagnosisName;
    },

    diagnosisName: function() {
      if (!this.patient || !this.patient.diagnosis) {
        return '';
      }

      return this.patient.diagnosis;
    },

    tc: function() {
      if (!this.patient) return '';

      return '<b>' + this.$t('ТЦ') + ': </b>' + this.patient.organizationCode;
    },
  },
};
</script>
